import { VFS, VFSError, GlobMatcher } from './vfs.js';
import { PathUtils } from './common.js';
import { uint8ArrayToBase64 } from '../utils.js';

/**
 * HTTP-backed virtual file system.
 *
 * Provides a read-only VFS implementation that resolves files via HTTP(S).
 * Supports:
 * - File reads via `GET`
 * - Existence/stat probing via `HEAD`
 * - Optional directory listing via pluggable {@link HttpDirectoryReader}s
 *
 * Limitations:
 * - This FS is read-only; mutating operations throw `VFSError` with code `"EROFS"`.
 * - Partial reads (HTTP range) are not implemented yet.
 *
 * @public
 */ class HttpFS extends VFS {
    baseOrigin;
    basePath;
    options;
    dirReaders;
    /**
   * Creates an HTTP file system rooted at `baseURL`.
   *
   * All relative VFS paths are resolved against `baseURL` and fetched using
   * the configured options.
   *
   * @param baseURL - Base URL of the HTTP root. Can be absolute or relative to `window.location`.
   * @param options - Optional HTTP and directory-reading configuration.
   */ constructor(baseURL, options = {}){
        super(true); // Readonly
        baseURL = baseURL || './';
        const url = new URL(baseURL, window.location.href);
        this.basePath = url.pathname;
        if (this.basePath.endsWith('/')) {
            this.basePath = this.basePath.slice(0, -1);
        }
        this.baseOrigin = url.origin === 'null' ? `${url.protocol}//${url.host}` : url.origin;
        this.options = {
            timeout: 30000,
            ...options
        };
        this.dirReaders = Array.isArray(options.directoryReader) ? options.directoryReader : options.directoryReader ? [
            options.directoryReader
        ] : [];
    }
    /**
   * Optional URL resolver hook that transforms VFS paths into concrete URLs.
   *
   * If present, it is applied by {@link VFS.normalizePath} before other checks.
   */ get urlResolver() {
        return this.options.urlResolver ?? null;
    }
    set urlResolver(resolver) {
        this.options.urlResolver = resolver;
    }
    /**
   * Normalizes a VFS path for HTTP use.
   *
   * Behavior:
   * - Applies `urlResolver` if provided.
   * - If the result is a data URI or an object URL, it is returned as-is.
   * - Otherwise falls back to the base VFS `normalizePath` logic.
   *
   * @param path - Input VFS path or URL-like string.
   * @returns Normalized path or URL.
   */ normalizePath(path) {
        if (this.options.urlResolver) {
            path = this.options.urlResolver(path);
        }
        path = path.trim();
        if (this.parseDataURI(path) || this.isObjectURL(path) || this.isAbsoluteURL(path)) {
            return path;
        }
        return super.normalizePath(path);
    }
    setReadonly(readonly) {
        if (!readonly) {
            console.error('Http VFS is always read-only');
        }
    }
    /** {@inheritDoc VFS._makeDirectory} */ async _makeDirectory(path) {
        throw new VFSError('HTTP file system is read-only', 'EROFS', path);
    }
    /** {@inheritDoc VFS._readDirectory} */ async _readDirectory(path, options) {
        const normalized = this.normalizePath(path);
        const dirPath = normalized.endsWith('/') ? normalized : normalized + '/';
        if (!this.dirReaders.length) {
            throw new VFSError('No HttpDirectoryReader configured for HttpFS', 'ENOTSUP', dirPath);
        }
        const ctx = {
            fetch: (url, init)=>this.fetchWithTimeout(url, init),
            toURL: (p)=>this.toAbsoluteURL(p),
            normalizePath: (p)=>super.normalizePath(p),
            joinPath: (...parts)=>PathUtils.join(...parts),
            guessMimeType: (name)=>this.guessMIMEType(name)
        };
        const reader = await this.selectReader(dirPath, ctx);
        if (!reader) {
            console.warn('No directory reader can handle this directory');
            return [];
        }
        let layer = await reader.readOnce(dirPath, ctx);
        const includeHidden = options?.includeHidden ?? false;
        layer = layer.filter((e)=>includeHidden || !e.name.startsWith('.'));
        if (options?.pattern) {
            const pattern = options.pattern;
            if (typeof pattern === 'string') {
                const matcher = new GlobMatcher(pattern, true);
                layer = layer.filter((e)=>matcher.test(e.name) || matcher.test(e.path));
            } else {
                layer = layer.filter((e)=>pattern.test(e.name) || pattern.test(e.path));
            }
        }
        if (!options?.recursive) {
            return layer;
        }
        const out = [
            ...layer
        ];
        for (const e of layer){
            if (e.type === 'directory') {
                try {
                    const sub = await this._readDirectory(e.path, options);
                    out.push(...sub);
                } catch  {
                // ignore
                }
            }
        }
        return out;
    }
    /** {@inheritDoc VFS._deleteDirectory} */ async _deleteDirectory(path) {
        throw new VFSError('HTTP file system is read-only', 'EROFS', path);
    }
    /** {@inheritDoc VFS._readFile} */ async _readFile(path, options) {
        try {
            const response = await this.fetchWithTimeout(path);
            if (!response.ok) {
                if (response.status === 404) {
                    throw new VFSError('File not found', 'ENOENT', path);
                } else if (response.status === 403) {
                    throw new VFSError('Access denied', 'EACCES', path);
                } else if (response.status >= 500) {
                    throw new VFSError('Server error', 'EIO', path);
                } else {
                    throw new VFSError(`HTTP error ${response.status}: ${response.statusText}`, 'EIO', path);
                }
            }
            if (options?.offset !== undefined || options?.length !== undefined) {
            // TODO: HTTP Range request
            }
            let data;
            if (options?.encoding === 'utf8') {
                data = await response.text();
            } else if (options?.encoding === 'base64') {
                const arrayBuffer = await response.arrayBuffer();
                const bytes = new Uint8Array(arrayBuffer);
                data = uint8ArrayToBase64(bytes);
            } else {
                data = await response.arrayBuffer();
            }
            return data;
        } catch (error) {
            if (error instanceof VFSError) {
                throw error;
            }
            throw new VFSError(`Failed to read file: ${error}`, 'EIO', path);
        }
    }
    /** {@inheritDoc VFS._writeFile} */ async _writeFile(path, _data, _options) {
        throw new VFSError('HTTP file system is read-only', 'EROFS', path);
    }
    /** {@inheritDoc VFS._deleteFile} */ async _deleteFile(path) {
        throw new VFSError('HTTP file system is read-only', 'EROFS', path);
    }
    /** {@inheritDoc VFS._exists} */ async _exists(path) {
        const normalizedPath = this.normalizePath(path);
        try {
            const response = await this.fetchWithTimeout(normalizedPath, {
                method: 'HEAD'
            });
            return response.ok;
        } catch  {
            return false;
        }
    }
    /** {@inheritDoc VFS._stat} */ async _stat(path) {
        try {
            const response = await this.fetchWithTimeout(path, {
                method: 'HEAD'
            });
            if (!response.ok) {
                if (response.status === 404) {
                    throw new VFSError('File not found', 'ENOENT', path);
                } else {
                    throw new VFSError(`HTTP error ${response.status}`, 'EIO', path);
                }
            }
            const size = parseInt(response.headers.get('content-length') || '0');
            const lastModified = response.headers.get('last-modified');
            const contentType = response.headers.get('content-type');
            const isDirectory = !!contentType && contentType.includes('text/html') && path.endsWith('/');
            const modifiedDate = lastModified ? new Date(lastModified) : new Date();
            return {
                size,
                isFile: !isDirectory,
                isDirectory,
                created: modifiedDate,
                modified: modifiedDate,
                accessed: modifiedDate
            };
        } catch (error) {
            if (error instanceof VFSError) {
                throw error;
            }
            throw new VFSError(`Failed to stat file: ${error}`, 'EIO', path);
        }
    }
    /** {@inheritDoc VFS._deleteFileSystem} */ async _deleteFileSystem() {
        return;
    }
    /** {@inheritDoc VFS._wipe} */ async _wipe() {
        return;
    }
    /** {@inheritDoc VFS._move} */ async _move() {
        throw new VFSError('HTTP file system is read-only', 'EROFS');
    }
    toAbsoluteURL(url) {
        // Specical URL case, just return
        if (this.isObjectURL(url) || this.parseDataURI(url) || this.isAbsoluteURL(url)) {
            return url;
        }
        // Split path, search parameters and hash
        const [pathPart, ...rest] = url.split('?');
        const queryAndHash = rest.join('?'); // Merge multiple '?'s
        const [query, hash] = queryAndHash ? queryAndHash.split('#') : [
            '',
            ''
        ];
        // Encode path
        const joinedPath = this.join(this.basePath, pathPart);
        const encodedPath = joinedPath.split('/').map((segment)=>{
            if (!segment || segment === '.' || segment === '..') {
                return segment;
            }
            // Avoid duplicated encoding
            try {
                return decodeURIComponent(segment) === segment ? encodeURIComponent(segment) : segment;
            } catch  {
                return encodeURIComponent(segment);
            }
        }).join('/');
        // Construct base URL
        const baseUrl = new URL(encodedPath, this.baseOrigin);
        // Add search parameters
        if (query) {
            baseUrl.search = query.includes('%') ? query : new URLSearchParams(query).toString();
        }
        // Add hash
        if (hash) {
            baseUrl.hash = hash;
        }
        return baseUrl.href;
    }
    async fetchWithTimeout(url, init) {
        const controller = new AbortController();
        const timeoutId = setTimeout(()=>controller.abort(), this.options.timeout);
        url = this.toAbsoluteURL(url);
        try {
            const response = await fetch(url, {
                ...init,
                signal: controller.signal,
                headers: {
                    ...this.options.headers,
                    ...init?.headers
                },
                credentials: this.options.credentials
            });
            return response;
        } finally{
            clearTimeout(timeoutId);
        }
    }
    async selectReader(dirPath, ctx) {
        if (this.dirReaders.length === 1) {
            return this.dirReaders[0];
        }
        for (const r of this.dirReaders){
            if (!r.canHandle) {
                continue;
            }
            try {
                if (await r.canHandle(dirPath, ctx)) {
                    return r;
                }
            } catch  {
            // ignore
            }
        }
        return null;
    }
}

export { HttpFS };
//# sourceMappingURL=httpfs.js.map
